import { useState, useEffect, useRef, useMemo } from 'react';
import { colorsButton } from 'app/colors';
import Icon from 'components/Kizen/Icon';
import Input from 'components/Kizen/Input';
import useField from 'hooks/useField';
import { KizenTypography } from 'app/typography';
import useModal from 'components/Modals/useModal';
import ConfirmationModal from 'components/Modals/presets/ConfirmationModal';
import { useTranslation } from 'react-i18next';
import Validation, { useValidation } from 'components/Inputs/Validation';
import { useTruncationTooltip } from 'components/Kizen/Tooltip';
import {
  CategoryHeader,
  CategoryWrapper,
  DeleteIconButton,
  EditableNameText,
  NewCategoryWrapper,
  StyledMigateFieldsToCategorySelect,
} from './styles';

export default function BuilderCategory(props) {
  const {
    category,
    categories,
    onConfirmDelete,
    onChangeName,
    handleProps,
    allowDeletion,
    children,
    readOnly,
    ...others
  } = props;
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [tooltipProps, tooltip] = useTruncationTooltip();
  const [catagoryToMigrateFieldsTo, setCategoryToMigrateFieldsTo] =
    useState(null);

  const categoryOptions = useMemo(
    () =>
      categories.reduce((acc, { id, name, fields }) => {
        if (id !== category.id) {
          acc.push({ value: id, label: name, fields });
        }
        return acc;
      }, []),
    [categories, category.id]
  );

  const [confirmDeletionModalProps, { onClick: showConfirmDeleteModal }] =
    useModal({
      handleSubmit: async () => {
        await onConfirmDelete(category, catagoryToMigrateFieldsTo);
        setCategoryToMigrateFieldsTo(null);
      },
    });

  const handleShowConfirmDeleteModal = () => {
    if (categoryOptions.length) {
      setCategoryToMigrateFieldsTo(categoryOptions[0]);
    }
    showConfirmDeleteModal();
  };

  const [stagedName, setStagedName] = useField(category.name, [category]);
  const wrapperRef = useRef();
  const [validation, validationProps] = useValidation(wrapperRef, {
    value: stagedName,
    validate: {
      full: (desc) => {
        if (!desc.trim()) {
          return t('This field is required');
        }
        if (onChangeName) {
          return onChangeName(stagedName, category);
        }
        return true;
      },
    },
  });

  const onFocus = () => {
    setEditing(true);
  };
  const onBlur = (...args) => {
    setEditing(false);
    validation.onBlur(...args);
  };

  return (
    <CategoryWrapper {...others}>
      <CategoryHeader>
        {readOnly ? (
          <span />
        ) : (
          <Icon icon="reorder" color={colorsButton.iconGray} {...handleProps} />
        )}
        {editing ? null : tooltip}
        <EditableNameText
          ref={wrapperRef}
          value={stagedName}
          onChange={setStagedName}
          {...validation}
          {...tooltipProps}
          onFocus={onFocus}
          onBlur={onBlur}
          disabled={readOnly}
        />
        <Validation inModal {...validationProps} />
        {readOnly ? (
          <span />
        ) : (
          <DeleteIconButton
            visible={allowDeletion}
            sizing="dense"
            title={t('Remove Category')}
            color={colorsButton.iconGray}
            onClick={handleShowConfirmDeleteModal}
          >
            <Icon icon="delete" />
          </DeleteIconButton>
        )}
      </CategoryHeader>
      {children}
      <ConfirmationModal
        buttonText={t('Confirm Delete')}
        heading={t('Please Confirm Deletion')}
        defaultLeftBtnText={t('Cancel')}
        {...confirmDeletionModalProps}
        disabled={!catagoryToMigrateFieldsTo}
      >
        {category.fields.length ? (
          <>
            <KizenTypography>
              {t(
                'This will permanently delete the {{name}} category. All fields in the category will be moved to the selected category.',
                category
              )}
              <StyledMigateFieldsToCategorySelect
                label={t('Choose Category')}
                value={catagoryToMigrateFieldsTo}
                options={categoryOptions}
                onChange={setCategoryToMigrateFieldsTo}
                menuInline
              />
            </KizenTypography>
          </>
        ) : (
          <KizenTypography>
            {t('This will permanently delete the Category.')}
          </KizenTypography>
        )}
      </ConfirmationModal>
    </CategoryWrapper>
  );
}

export function NewCategory({ onConfirmCreate, ...others }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [modalProps, triggerProps, modal] = useModal({
    handleSubmit: async () => {
      await onConfirmCreate({ name });
    },
  });
  useEffect(() => {
    setName('');
  }, [modal.showing]);

  return (
    <>
      <NewCategoryWrapper {...triggerProps} {...others}>
        <KizenTypography
          as="div"
          weight="bold"
          size="buttonLabel"
          textTransform="uppercase"
          color="inherit"
        >
          {t('Add New Category')}
        </KizenTypography>
        <KizenTypography as="div" size="48px" color="inherit">
          +
        </KizenTypography>
      </NewCategoryWrapper>
      <ConfirmationModal
        heading={t('Add Category')}
        leftBtn={false}
        {...modalProps}
      >
        <div style={{ textAlign: 'left' }}>
          <Input
            autoFocus
            label={t('Category Name')}
            placeholder={t('Enter Category Name')}
            value={name}
            onChange={setName}
          />
        </div>
      </ConfirmationModal>
    </>
  );
}
